import {
  Alert,
  Button,
  Chip,
  Col,
  DisplayValue,
  Loading,
  Panel,
  Row,
  SectionTitle
} from '@elotech/components';
import { VerificacaoDocumentosService } from 'itbi-common/service';
import { UserHooks } from 'itbi-common/state/user';
import React, { useEffect, useState } from 'react';

import DocumentosFormulario from './DocumentosFormulario';
import SolicitacaoDocumentosTable from './SolicitacaoDocumentosTable';
import {
  DocumentoValidacaoNivel,
  ItemFormulario,
  Solicitacao,
  hoje,
  podeReenviar,
  situacaoColor,
  situacaoLabel
} from './verificacaoDocumentos';

const VerificacaoDocumentosSection: React.FC = () => {
  const envioDocumentosHabilitado = UserHooks.useEnvioDocumentosHabilitado();
  const [loading, setLoading] = useState(false);
  const [documentos, setDocumentos] = useState<DocumentoValidacaoNivel[]>([]);
  const [solicitacao, setSolicitacao] = useState<Solicitacao | null>(null);
  const [modoReenvio, setModoReenvio] = useState(false);
  const [arquivos, setArquivos] = useState<{ [documentoId: string]: File }>({});
  const [dadosDocumento, setDadosDocumento] = useState<{
    [documentoId: string]: { numero?: string; emitidoEm?: string };
  }>({});

  const carregar = () => {
    setLoading(true);
    Promise.all([
      VerificacaoDocumentosService.documentoValidacaoNivel(),
      VerificacaoDocumentosService.minhaSolicitacao().catch(() => ({
        data: null
      }))
    ])
      .then(([documentosResponse, solicitacaoResponse]) => {
        setDocumentos(
          [...documentosResponse.data].sort((a, b) => a.ordem - b.ordem)
        );
        setSolicitacao(solicitacaoResponse.data);
      })
      .catch(error => {
        Alert.error(
          { title: 'Não foi possível carregar os documentos necessários.' },
          error
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (envioDocumentosHabilitado) {
      carregar();
    }
  }, [envioDocumentosHabilitado]);

  const itensEnvio: ItemFormulario[] = documentos.map(documento => ({
    key: documento.id,
    nivel: documento
  }));

  const itensReenvio: ItemFormulario[] = (solicitacao?.documentos || [])
    .filter(usuarioDocumento => podeReenviar(usuarioDocumento.situacao))
    .map(usuarioDocumento => ({
      key: usuarioDocumento.id,
      nivel: usuarioDocumento.documentoValidacaoNivel
    }));

  const onChangeArquivo = (documentoId: string, file?: File) => {
    setArquivos(prev => {
      if (file) {
        return { ...prev, [documentoId]: file };
      }
      const { [documentoId]: removido, ...restantes } = prev;
      return restantes;
    });
  };

  const onChangeDado = (
    documentoId: string,
    campo: 'numero' | 'emitidoEm',
    valor: string
  ) => {
    setDadosDocumento(prev => ({
      ...prev,
      [documentoId]: { ...prev[documentoId], [campo]: valor }
    }));
  };

  const validaFormulario = (itens: ItemFormulario[]): boolean => {
    const obrigatoriosFaltando = itens.some(
      item => item.nivel.obrigatorio && !arquivos[item.key]
    );
    if (obrigatoriosFaltando) {
      Alert.warning({
        title: 'Envie todos os documentos obrigatórios antes de continuar.'
      });
      return false;
    }

    const informacoesFaltando = itens.some(item => {
      const dados = dadosDocumento[item.key];
      return (
        arquivos[item.key] &&
        ((item.nivel.possuiNumero && !dados?.numero) ||
          (item.nivel.possuiEmitidoEm && !dados?.emitidoEm))
      );
    });
    if (informacoesFaltando) {
      Alert.warning({
        title:
          'Preencha todas as informações dos documentos anexados antes de continuar.'
      });
      return false;
    }

    const emitidoEmFuturo = itens.some(item => {
      const emitidoEm = dadosDocumento[item.key]?.emitidoEm;
      return emitidoEm && emitidoEm > hoje();
    });
    if (emitidoEmFuturo) {
      Alert.warning({
        title: 'A data de emissão não pode ser maior que a data de hoje.'
      });
      return false;
    }

    if (!itens.some(item => arquivos[item.key])) {
      Alert.warning({
        title: 'Anexe ao menos um documento antes de continuar.'
      });
      return false;
    }

    return true;
  };

  const toDocumentoDto = (item: ItemFormulario) => ({
    documentoValidacaoNivel: {
      id: item.nivel.id,
      documentoSolicitado: item.nivel.documentoSolicitado
    },
    tipo: item.nivel.documentoSolicitado,
    numero: dadosDocumento[item.key]?.numero || undefined,
    emitidoEm: dadosDocumento[item.key]?.emitidoEm || undefined
  });

  const onEnviar = () => {
    if (!validaFormulario(itensEnvio)) {
      return;
    }

    const enviados = itensEnvio.filter(item => arquivos[item.key]);
    const dto = { documentos: enviados.map(toDocumentoDto) };
    const files = enviados.map(item => arquivos[item.key]);

    setLoading(true);
    VerificacaoDocumentosService.enviarSolicitacao(dto, files)
      .then(() => {
        Alert.success({ title: 'Documentos enviados com sucesso!' }).then(() =>
          window.location.reload()
        );
      })
      .catch(error => {
        Alert.error({ title: 'Não foi possível enviar os documentos.' }, error);
      })
      .finally(() => setLoading(false));
  };

  const onReenviarDocumentos = () => {
    if (!validaFormulario(itensReenvio)) {
      return;
    }

    const enviados = itensReenvio.filter(item => arquivos[item.key]);
    const dtos = enviados.map(item => ({
      id: item.key,
      ...toDocumentoDto(item)
    }));
    const files = enviados.map(item => arquivos[item.key]);

    setLoading(true);
    VerificacaoDocumentosService.reenviarDocumentos(dtos, files)
      .then(() => {
        Alert.success({
          title: 'Documentos reenviados com sucesso!'
        }).then(() => window.location.reload());
      })
      .catch(error => {
        Alert.error(
          { title: 'Não foi possível reenviar os documentos.' },
          error
        );
      })
      .finally(() => setLoading(false));
  };

  const onCancelarReenvio = () => {
    setModoReenvio(false);
    setArquivos({});
    setDadosDocumento({});
  };

  if (
    !envioDocumentosHabilitado ||
    (!loading && documentos.length === 0 && !solicitacao)
  ) {
    return null;
  }

  return (
    <div>
      <Loading loading={loading} />
      <SectionTitle>Documentos para Acesso Eletrônico</SectionTitle>

      {!solicitacao && documentos.length > 0 && (
        <Panel>
          <DocumentosFormulario
            itens={itensEnvio}
            arquivos={arquivos}
            onChangeArquivo={onChangeArquivo}
            onChangeDado={onChangeDado}
          />
          <Row>
            <Col md={12} style={{ textAlign: 'right' }}>
              <Button className="inline" disabled={loading} onClick={onEnviar}>
                Enviar documentos
              </Button>
            </Col>
          </Row>
        </Panel>
      )}

      {solicitacao && (
        <>
          <Row>
            <DisplayValue
              size={4}
              label="Situação da solicitação"
              value={
                <Chip
                  inline
                  value={
                    situacaoLabel[solicitacao.status] || solicitacao.status
                  }
                  color={situacaoColor[solicitacao.status]}
                />
              }
            />
            {solicitacao.status === 'REJEITADO' &&
              solicitacao.motivoRejeicao && (
                <DisplayValue
                  size={8}
                  label="Motivo da rejeição"
                  value={solicitacao.motivoRejeicao}
                />
              )}
          </Row>
          {solicitacao.status === 'PENDENTE' && (
            <p className="help-message">
              Sua solicitação está em análise. Não é possível enviar novos
              documentos até a conclusão.
            </p>
          )}

          {!modoReenvio && (
            <SolicitacaoDocumentosTable
              documentos={solicitacao.documentos || []}
              onReenviar={() => setModoReenvio(true)}
            />
          )}

          {modoReenvio && (
            <Panel title="Reenvio de documentos">
              <DocumentosFormulario
                itens={itensReenvio}
                arquivos={arquivos}
                onChangeArquivo={onChangeArquivo}
                onChangeDado={onChangeDado}
              />
              <Row>
                <Col md={12} style={{ textAlign: 'right' }}>
                  <Button
                    className="inline"
                    color="neutral"
                    disabled={loading}
                    onClick={onCancelarReenvio}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="inline"
                    disabled={loading}
                    onClick={onReenviarDocumentos}
                  >
                    Reenviar documentos
                  </Button>
                </Col>
              </Row>
            </Panel>
          )}
        </>
      )}
    </div>
  );
};

export default VerificacaoDocumentosSection;
